// `keyboardNavigation` is a helper to handle input field + list of
// suggestion type of UI. It provides intuitive arrow navigation
// between the input field and options, transferring focus between
// them. Works best if each option is a button.
//
// `keyboardNavigation` should not be instantiated multiple times
// during the lifespan of an entity and not used inside render
// function
//
// This function returns an object with two functions:
//
// - `inputRef` which should be called with input dom element,
//   normally passed to `ref` attribute of an input field
// - `itemRef` should be called with index of option and returns a
//   function to be called with dom element, this way it's convenient
//   to use when passing to `ref`
//
// Example:
// ```js
// const comp = withKeyboardNavigation(({
//   options = [],
//   keyboardNavigation: { inputRef, itemRef }
// }) => (
//   <div>
//     <input ref={inputRef}>
//     {options.map((value, index) =>
//       <button ref={itemRef(index)} key={value}>value</button>
//     )}
//   </div>
// );
// ```

import PropTypes from 'prop-types';
import React, { Component, ComponentType, ReactNode, FunctionComponent } from 'react';
import keycode from 'keycode';
import { refType } from 'app/utils/propTypes';

export const keyboardNavigationShape = PropTypes.shape({
  inputRef: refType.isRequired,
  itemRef: PropTypes.func.isRequired, // fn that returns a ref
});

// Buttons that don't cause jumping from an item to the input field
const itemNonescapeButtons = ['left command', 'right command', 'alt', 'tab', 'shift', 'ctrl'];

interface IItem {
  node: HTMLElement;
  handler: (event: KeyboardEvent) => void;
}

export interface IChildParams {
  inputRef: (node: HTMLInputElement | null) => void;
  itemRef: (index: number) => (node: HTMLElement | null) => void;
}

interface IOptions {
  focusInputAfterSelect?: boolean;
  autofocusInput?: boolean;
}

interface IProps extends IOptions {
  children: (params: IChildParams) => ReactNode;
}

export default class KeyboardNavigation extends Component<IProps> {
  public static defaultProps = {
    focusInputAfterSelect: false,
    autofocusInput: false,
  };

  private input: HTMLInputElement | null = null;
  private items: Array<IItem | null> = [];

  public render() {
    const { inputRef, itemRef } = this;
    return this.props.children({ inputRef, itemRef });
  }

  private inputRef = (newInput: HTMLInputElement | null) => {
    if (this.input) {
      this.input.removeEventListener('keydown', this.handleInputKeyboard, false);
    }

    if (newInput) {
      this.input = newInput;
      this.input.addEventListener('keydown', this.handleInputKeyboard, false);

      if (this.props.autofocusInput) {
        this.input.focus();
      }
    } else {
      this.input = null;
    }
  };

  private itemRef = (index: number) => (newNode: HTMLElement | null) => {
    const { items } = this;
    const item = items[index];
    if (item) {
      const { node, handler } = item;
      node.removeEventListener('keydown', handler, false);
    }

    if (newNode) {
      const handler = this.handleItemKeyboard.bind(null, index);
      newNode.addEventListener('keydown', handler, false);

      items[index] = { node: newNode, handler };
    } else {
      items[index] = null;
    }
  };

  private handleInputKeyboard = (event: KeyboardEvent) => {
    const { items } = this;
    const key = keycode(event);

    if (key === 'up') {
      // Pressing arrow up should focus last element on list
      event.preventDefault();

      for (let i = items.length - 1; i > 0; --i) {
        // Find last non-null item
        const item = items[i];
        if (item) {
          item.node.focus();
          break;
        }
      }
    } else if (key === 'down') {
      // Pressing the down arrow on input field will focus the select
      // list.
      event.preventDefault();

      this.focusItem(items[0]);
    }
  };

  private handleItemKeyboard = (index: number, event: KeyboardEvent) => {
    const { input, items } = this;
    const key = keycode(event);

    if (key === 'up') {
      // Pressing arrow up will navigate one item up or to the input
      // field
      event.preventDefault();

      if (index === 0) {
        if (input) {
          input.focus();
        } else {
          this.focusItem(items[items.length - 1]);
        }
      } else {
        this.focusItem(items[index - 1]);
      }
    } else if (key === 'down') {
      // Focus one item down or wrap around to input field
      event.preventDefault();

      if (items[index + 1]) {
        this.focusItem(items[index + 1]);
      } else if (input) {
        input.focus();
      } else {
        this.focusItem(items[0]);
      }
    } else if (['space', 'enter'].indexOf(key) >= 0) {
      // Focus input field after confirming selection
      if (this.props.focusInputAfterSelect) {
        setTimeout(() => input && input.focus());
      }
    } else if (itemNonescapeButtons.indexOf(key) === -1) {
      // Focus input field on typing (the letter will be entnered to the field)
      if (input) {
        input.focus();
      }
    }
  };

  private focusItem(item: IItem | null) {
    if (item) {
      item.node.focus();
    }
  }
}

export function withKeyboardNavigation<T extends { keyboardNavigation: IChildParams }>(
  Wrap: ComponentType<T>, options: IOptions,
) {
  const Connected: FunctionComponent<Omit<T, 'keyboardNavigation'>> = (props) => (
    <KeyboardNavigation {...options}>{(keyboardNavigation) =>
      // TYPESCRIPT: Work around typescript limitation with generics. See
      // https://github.com/Microsoft/TypeScript/issues/28938
      <Wrap {...props as T} keyboardNavigation={keyboardNavigation} />
    }</KeyboardNavigation>
  );
  Connected.displayName = `withKeyboardNavigation(${Wrap.displayName})`;
  return Connected;
}
